import { useEffect, useState, type RefObject } from 'react';

/**
 * FIX-B38 (QX-801): true once the element is in (or near) the viewport, and it
 * stays true. A conversation with many images fetches each image's bytes only
 * when it is about to be seen, instead of all of them at once.
 * Without IntersectionObserver (old browser / test env) it is true at once.
 */
export function useNearViewport<T extends Element>(ref: RefObject<T | null>, enabled = true, rootMargin = '200px'): boolean {
  const supported = typeof window !== 'undefined' && typeof window.IntersectionObserver === 'function';
  const [seen, setSeen] = useState(!enabled || !supported);
  useEffect(() => {
    if (seen || !enabled || !supported) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    const io = new window.IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setSeen(true);
        io.disconnect();
      }
    }, { rootMargin });
    io.observe(el);
    return () => io.disconnect();
  }, [ref, seen, enabled, supported, rootMargin]);
  return seen || !enabled || !supported;
}
